/**
 * Shaping the shopper's context attributes for the contact panel.
 *
 * The backend writes a {"type": "context", "payload": {...}} block on every
 * customer message: the attributes that shopper's browser sent with that turn,
 * already filtered server side (platform machinery and the tenant's identity
 * cookie never reach us) and capped at 20 keys and 4096 bytes.
 *
 * Two things follow from where the data comes from. It is a record per turn and
 * never a stored property of the contact, so the panel reads the NEWEST block
 * and the heading says as much. And it is written by the visitor's browser, so
 * nothing here trusts a type, a shape or a length: the server's cap is on the
 * map, not on each value, and a single value can still be four thousand bytes
 * of anything.
 *
 * Deliberately generic: only the platform's own keys (current_page_url,
 * page_language and cart) are special cased upstream. A key that nobody wrote code
 * for still has to show up, otherwise an attribute stays invisible until someone
 * ships a translation string for it.
 *
 * Pure: no React, no network, same input gives the same output.
 */

// Cem caracteres enchem tres linhas na coluna de 20rem. O resto vai para o
// `title`, nao para o ecra: o valor comprido e do cliente, e um deles sozinho
// empurrava o historico para fora da vista.
const MAX_VALUE_CHARS = 100;

// O mesmo raciocinio para a lista encaixada: um objeto com trinta chaves (os
// tamanhos de um perfil, por exemplo) nao pode ser trinta partes numa linha.
export const MAX_NESTED_ENTRIES = 6;

export const isScalar = (value) =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean";

// Vazio nao e informacao: uma chave a null ou uma string em branco so ensinava o
// operador que ha campos que nao dizem nada. Um array ou objeto vazio fica, que
// esse conta uma historia ("carrinho: 0 itens").
const isRenderable = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return true;
  return typeof value === "object";
};

const renderableEntries = (obj) => Object.entries(obj).filter(([, v]) => isRenderable(v));

/**
 * The newest context block in the conversation, as [key, value] pairs.
 *
 * Returns null when there is none, which is every conversation recorded before
 * the backend ships this block, and also when the newest one carries nothing
 * worth drawing. The caller renders nothing at all in that case: no heading and
 * no empty state, because a section that says "no attributes" teaches the
 * operator that something is missing when nothing is.
 *
 * Newest is taken by array order, the same order the transcript renders the
 * messages in. If that order were wrong the whole screen would already be wrong.
 */
export const latestContextAttributes = (conversation) => {
  const messages = conversation?.messages;
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const blocks = messages[i]?.blocks;
    if (!Array.isArray(blocks)) continue;
    const block = [...blocks].reverse().find((b) => b?.type === "context");
    if (!block) continue;
    const payload = block.payload;
    // Um payload que nao seja um mapa nao se desenha, e nao se vai buscar o
    // bloco anterior para tapar o buraco: o mais recente e a resposta.
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
    const entries = renderableEntries(payload);
    return entries.length ? entries : null;
  }
  return null;
};

/**
 * The newest customer profile in the conversation, as [key, value] pairs (KAN-41).
 *
 * What the agent was told about the customer on that turn: the CDP profile, and on
 * MODZ the size section from its cookie. The backend writes it only on customer
 * messages and only when there was something to tell, so a newer message without
 * one (a follow-up, a cleared cookie) does not hide the last one that had it.
 * Null when there is none, and the caller then draws nothing, heading included.
 */
export const latestCustomerProfile = (conversation) => {
  const messages = conversation?.messages;
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const profile = messages[i]?.customer_profile;
    if (!profile || typeof profile !== "object" || Array.isArray(profile)) continue;
    const entries = renderableEntries(profile);
    return entries.length ? entries : null;
  }
  return null;
};

/**
 * The device of the newest customer message that has one (RPAA-286), or null.
 *
 * The backend classifies it per message, from the User-Agent and what the widget
 * read in the browser, so like the profile a newer message without one (sent by a
 * widget from before this, or by another transport) does not hide the last one
 * that had it. Null for every conversation recorded before the backend wrote it.
 */
export const latestClient = (conversation) => {
  const messages = conversation?.messages;
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const client = messages[i]?.client;
    if (client && typeof client === "object" && !Array.isArray(client)) return client;
  }
  return null;
};

/**
 * "iOS 26", "Safari 26", "macOS": the name, with the major version when there is
 * one. A version the browser froze arrives as null and is left out rather than
 * guessed.
 */
export const withVersion = (name, version) => (version ? `${name} ${version}` : name);

/**
 * A readable label out of the key itself, with no per key translation.
 *
 * loyalty_tier gives "Loyalty tier", cartTotal gives "Cart total". The keys are
 * English on the wire in both languages, and inventing a dictionary here would
 * mean an attribute stays invisible until someone ships a string for it.
 */
export const humanizeKey = (key) => {
  const raw = String(key);
  // Uma chave que nao se le como identificador (espaco, hifen, letra fora do ASCII:
  // "T-shirt", "Size profile", "Preferências") ja esta escrita para pessoas e so
  // ganha a primeira maiuscula: o humanizador estragava-a ("T shirt"). Um
  // identificador (so ASCII, digitos, `_` e `.`) humaniza-se como sempre, incluindo
  // "CartTotal" e "Loyalty_Tier".
  if (!/^[A-Za-z0-9_.]+$/.test(raw)) return raw.charAt(0).toUpperCase() + raw.slice(1);
  const spaced = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    // As corcovas do camelCase davam "Newsletter Opt In" ao lado de "Loyalty
    // tier", duas capitalizacoes diferentes na mesma coluna conforme a chave.
    // Uma palavra com maiuscula seguida de minusculas desce; SKU ou EUR ficam.
    .replace(/\b[A-Z][a-z]+\b/g, (word) => word.toLowerCase());
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * What to draw and what to hide in the title attribute.
 *
 * Whitespace collapses first: newlines are free for the client to send and a
 * value with forty of them owns the panel.
 */
export const clipText = (value, max = MAX_VALUE_CHARS) => {
  const full = String(value);
  const flat = full.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return { shown: flat, title: flat === full ? undefined : full };
  return { shown: `${flat.slice(0, max)}…`, title: full };
};

// A denylist and not an allowlist, because which parameters matter is a tenant's
// secret and which ones are tracking is the same everywhere. Compared in lower case.
const TRACKING_PREFIX = "utm_";
const TRACKING_PARAMS = new Set([
  "gclid", "gbraid", "wbraid", "dclid", "fbclid", "msclkid", "yclid", "igshid",
  "ttclid", "twclid", "li_fat_id", "srsltid", "mc_cid", "mc_eid", "_ga", "_gl",
]);

const isTracking = (key) => {
  const lower = key.toLowerCase();
  return lower.startsWith(TRACKING_PREFIX) || TRACKING_PARAMS.has(lower);
};

// Decoded, in the original order, a key with no value shown as just the key.
// Um `&`, `=`, `#` ou `%` que veio descodificado voltava a ler-se como parametros a
// mais (`q=a%26b` mostrava `q=a&b`): estes quatro voltam a codificar-se.
const encodeDisplay = (text) => text.replace(/[&=#%]/g, (c) => encodeURIComponent(c));

const displayQuery = (url) => {
  const kept = [...url.searchParams].filter(([key, value]) => !isTracking(key) && (key !== "" || value !== ""));
  return kept.length
    ? `?${kept.map(([key, value]) => (value ? `${encodeDisplay(key)}=${encodeDisplay(value)}` : encodeDisplay(key))).join("&")}`
    : "";
};

// Os caracteres de controlo bidi que a query descodificada pode trazer viram o
// sentido do texto do rotulo: saem dele (o href e o title ficam como estao). Os
// embeddings, overrides e isolates (U+202A a U+202E, U+2066 a U+2069) e as marcas
// LRM, RLM e ALM (U+200E, U+200F, U+061C), que so reordenam a pontuacao vizinha.
// O `pageLabel` (utils/analyticsPages.js) tira o mesmo conjunto dos caminhos das paginas.
export const NO_BIDI = /[\u202A-\u202E\u2066-\u2069\u200E\u200F\u061C]/g;

// Se o URL e de uma das origens do chat: igualdade exata, como o
// `_check_origin_allowed`. Sem a lista nao se afirma que e a loja.
export const isStoreOrigin = (url, storeOrigins) => Array.isArray(storeOrigins) && storeOrigins.includes(url.origin);

// O caminho de um URL como se le, sem a query nem o fragmento. Na homepage o
// caminho e so "/", que nao se le como nada, e ai vale mais o dominio.
// O valor vem do browser do visitante e o backend so lhe verifica o esquema: sem
// o host, um visitante podia mandar `https://outro.example/carrinho` e o operador
// lia "/carrinho" como se fosse a loja. O host sai do rotulo so quando a origem
// e uma das do chat (`isStoreOrigin`). Na homepage o host ja la estava.
// Tambem o rotulo da marca de pagina do visualizador (utils/conversationPages.js).
export const displayPath = (url, storeOrigins) => {
  const isStore = isStoreOrigin(url, storeOrigins);
  return url.pathname && url.pathname !== "/" ? (isStore ? "" : url.host) + url.pathname : `${url.host}/`;
};

/**
 * The link behind current_page_url, or null to fall back to plain text.
 *
 * The URLs carry ?utm_source=chat&utm_medium=chatbot&utm_campaign=chat_tracking,
 * three wrapped lines that tell an operator nothing, so the label drops the
 * tracking parameters (TRACKING_PARAMS) and keeps every other one: what is useful
 * differs per tenant (`?q=tv` is the page), what is tracking does not. The
 * #fragment stays too: in a single-page app it is the page (`/#/checkout`). The
 * whole, untouched URL lives in the href and the title. The host leads the label
 * unless the page is one of the chat's own origins (`storeOrigins`).
 *
 * Also the gate for the product card's cta_url (utils/productCards.js): both
 * values arrive on an untrusted payload and both end up in an href on the
 * operator's authenticated screen, so the screen has one convention for what
 * may become a link, not two. `path` is ignored by that caller.
 */
export const parsePageUrl = (value, storeOrigins) => {
  if (typeof value !== "string") return null;
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  // Sem este teste um valor do browser do visitante virava um href executavel no
  // ecra do operador: `javascript:` e `data:` sao URLs validos para o construtor.
  // Fora de http e https o valor cai no texto simples, que e inerte.
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const path = (displayPath(url, storeOrigins) + displayQuery(url) + url.hash).replace(NO_BIDI, "");
  return { href: url.href, path };
};

/**
 * One level down from the top: the entries of an object, or the elements of an
 * array numbered from one.
 */
export const nestedEntries = (value) => {
  if (Array.isArray(value)) {
    return value.map((v, i) => [String(i + 1), v]).filter(([, v]) => isRenderable(v));
  }
  return renderableEntries(value);
};

/**
 * Two levels down everything collapses to a count. Dumping JSON at an operator
 * is not a rendering.
 */
export const collapsedCount = (value) => ({
  isList: Array.isArray(value),
  count: Array.isArray(value) ? value.length : Object.keys(value).length,
});

/**
 * The name of a BCP 47 language tag written in the backoffice's language
 * ("en-US" in pt is "inglês (Estados Unidos)"). A tag Intl rejects, or one it
 * has no name for, comes back as it arrived: the operator still reads
 * something, and a wrong name would be worse than a raw code.
 */
// "standard" porque por omissao o Intl em ingles da o dialeto ("American
// English", "European Portuguese"), que ja nao se parece com o codigo da tag.
export const languageName = (code, displayLanguage) => {
  try {
    return new Intl.DisplayNames([displayLanguage], { type: "language", languageDisplay: "standard" }).of(code) || code;
  } catch (error) {
    return code;
  }
};

// A forma de uma tag de lingua e o tamanho maximo, a mesma regra do `_LANGUAGE` e do
// `_MAX_LANGUAGE_LENGTH` de services/device_classifier.py do backend. A
// `page_language` vem do browser do visitante e o backend nao a valida: so o que tem
// forma de tag se trata como lingua, o resto cai na lista generica, onde se corta.
const LANGUAGE_TAG = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{1,8}){0,7}$/;
const MAX_LANGUAGE_CHARS = 35;

export const isLanguageTag = (value) =>
  typeof value === "string" && value.length <= MAX_LANGUAGE_CHARS && LANGUAGE_TAG.test(value);

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Cart lines that are articles, leaving out the lines that are a chosen add-on.
 *
 * Replica de `_cart_articles` em core/prompt_config.py do backend: as duas tem de
 * mudar juntas, senao o painel e o agente contam artigos diferentes. A Radio
 * Popular lista um extra escolhido duas vezes (no `addons_selected` do artigo e
 * como linha propria cujo `id` e o `item` do extra). Uma linha dobra so quando nao
 * tem extras proprios e o seu `id` e o `item` de um extra que algum artigo
 * escolheu, e cada escolha dobra no maximo uma linha.
 */
export const cartArticles = (items) => {
  const unclaimed = new Map();
  items.forEach((item) => {
    (Array.isArray(item.addons_selected) ? item.addons_selected : []).forEach((addon) => {
      if (isPlainObject(addon)) unclaimed.set(addon.item, (unclaimed.get(addon.item) || 0) + 1);
    });
  });
  const hasList = (list) => Array.isArray(list) && list.length > 0;
  return items.filter((item) => {
    const lineId = item.id === null || item.id === undefined ? "" : String(item.id).trim();
    const hasOwnAddons = hasList(item.addons_available) || hasList(item.addons_selected);
    if (!hasOwnAddons && (unclaimed.get(lineId) || 0) > 0) {
      unclaimed.set(lineId, unclaimed.get(lineId) - 1);
      return false;
    }
    return true;
  });
};

/**
 * What may still be offered for one article: each available add-on the customer has
 * not selected, and none of a kind they already chose one of.
 *
 * Porta de `_suggested_addons` em core/prompt_config.py do backend (e a mesma lista
 * que o agente recebe como `addons_suggested`): as duas tem de mudar juntas. Os
 * objetos casam por `item`, nunca por nome nem pelo objeto inteiro (o site ja
 * escreveu a mesma instalacao de duas maneiras). O tipo e a `designation`: um artigo
 * leva uma garantia, por isso escolhida uma, as outras garantias nao sao uma venda
 * adicional. As palavras do vocabulario (texto) casam inteiras.
 */
export const suggestedAddons = (article) => {
  const selected = Array.isArray(article.addons_selected) ? article.addons_selected : [];
  const available = Array.isArray(article.addons_available) ? article.addons_available : [];
  const selectedItems = new Set(selected.filter(isPlainObject).map((addon) => addon.item));
  const chosenKinds = new Set(selected.filter((addon) => isPlainObject(addon) && addon.designation).map((addon) => addon.designation));
  return available.filter((addon) => {
    if (typeof addon === "string") return !selected.includes(addon);
    return isPlainObject(addon) && !selectedItems.has(addon.item) && !chosenKinds.has(addon.designation);
  });
};

// O carrinho vem do browser do visitante e o painel desenha-o, por isso nada dele
// chega ao JSX sem passar por aqui: o que nao e texto nem numero nao e um nome (um
// objeto como filho de um elemento deitava a pagina inteira abaixo), e um nome
// comprido corta-se como qualquer valor.
const cartText = (raw) => {
  if (typeof raw !== "string" && typeof raw !== "number") return { name: "" };
  const text = String(raw).trim();
  if (!text) return { name: "" };
  const { shown, title } = clipText(text);
  return { name: shown, title };
};

/**
 * A money amount sent as a number or as numeric text, else undefined.
 *
 * Porta de `_parse_amount` em api/chatwoot_webhook.py: a Radio Popular escreve os
 * precos dos extras como texto ("20.00") em carrinhos onde os das linhas sao
 * numeros. Um booleano nao e valor, nem texto que nao se le como decimal (o `float`
 * do Python nao aceita "0x10", e o `Number` do JS aceitaria), nem nan nem infinito.
 */
export const parseAmount = (raw) => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== "string") return undefined;
  const text = raw.trim();
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/.test(text)) return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
};

/**
 * One item's add-on list as the backend leaves it (`_normalize_cart_addons`): a
 * vocabulary word kept as it came, an object only when it has an `item` (the
 * refused warranty "Nao, Obrigado." has none), the `item` as trimmed text and the
 * price as a number or gone. Anything else is dropped.
 */
const normalizeAddons = (entries) => {
  if (!Array.isArray(entries)) return [];
  const normalized = [];
  entries.forEach((entry) => {
    if (typeof entry === "string") {
      normalized.push(entry);
      return;
    }
    if (!isPlainObject(entry)) return;
    const code = entry.item === null || entry.item === undefined ? "" : String(entry.item).trim();
    if (!code) return;
    const addon = { ...entry, item: code };
    const price = parseAmount(entry.price);
    if (price === undefined) delete addon.price;
    else addon.price = price;
    normalized.push(addon);
  });
  return normalized;
};

const normalizeItem = (item) => {
  const normalized = { ...item };
  ["addons_available", "addons_selected"].forEach((key) => {
    if (key in item) normalized[key] = normalizeAddons(item[key]);
  });
  return normalized;
};

// O que se desenha de um extra: nome (so texto) e preco (so numero). Um extra sem
// nome legivel nao se desenha.
const addonDisplay = (addon) => {
  if (typeof addon === "string") return cartText(addon);
  const display = cartText(addon.name);
  return { ...display, price: addon.price };
};

// Tamanhos que o painel mostra do carrinho: so as primeiras linhas, e no maximo
// vinte quando se abre o resto. Um carrinho de cinquenta linhas nao pode ser
// cinquenta linhas no painel.
export const CART_ARTICLES_SHOWN = 5;
export const MAX_CART_ARTICLES = 20;

export const isMoney = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * The cart the widget sends under the platform key `cart` (RPAA-190), or null
 * when the value does not read as one, and the caller then draws the raw value
 * as it did before. The value is JSON text only, as in the backend's
 * `_resolve_cart` (an object there gives no cart, so the agent sees none): an
 * object {items: [...], total?}, where a missing or null `items` is an empty cart,
 * which is what the agent reads too ("No items in cart").
 *
 * Replica o que o backend faz antes de o agente ler o carrinho, e as funcoes
 * mudam juntas: `_resolve_cart`, `_normalize_cart_addons`, `_parse_amount`,
 * `_cart_articles` e `_suggested_addons` (api/chatwoot_webhook.py e
 * core/prompt_config.py). So o nome em falta (que o backend vai buscar ao
 * catalogo) fica por resolver: o componente mostra "Artigo {id}". Fiel nos
 * carrinhos que o `JSON.stringify` do widget produz; divergem tres coisas que ele
 * nunca escreve: um inteiro escrito como decimal (`2.0`, que o Python conta como
 * float), um booleano no id (`"True"` no Python, `"true"` aqui) e grafias de numero
 * que so o `float` do Python aceita (`"1_000"`, digitos nao ASCII).
 *
 * Returns display-ready {articles, count, total}: articles with add-on lines folded,
 * each {id, name, nameTitle, qty, price, priceText, priceTitle, chosen, toOffer},
 * the sum of their quantities (a missing or non integer qty counts 1, as the
 * backend does) and the host's total when it is a number or numeric text
 * (parseAmount). A line price that is not a number (`"219,99"`) has no `price`: the
 * agent reads it as it came, so `priceText` carries it, clipped.
 */
export const parseCart = (value) => {
  if (typeof value !== "string") return null;
  let cart;
  try {
    cart = JSON.parse(value);
  } catch (error) {
    return null;
  }
  if (!isPlainObject(cart)) return null;
  // Sem `items` (ou a null) o backend le um carrinho vazio, nao um carrinho
  // estragado: o agente recebe a seccao neutra.
  if (cart.items === undefined || cart.items === null) return { articles: [], count: 0, total: undefined };
  if (!Array.isArray(cart.items) || !cart.items.every(isPlainObject)) return null;
  const articles = cartArticles(cart.items.map(normalizeItem)).map((item) => {
    const { name, title } = cartText(item.name);
    const id = item.id === null || item.id === undefined ? "" : String(item.id).trim();
    const price = parseAmount(item.price);
    const priceText = price === undefined ? cartText(item.price) : { name: "" };
    const shownAddons = (list) => list.map(addonDisplay).filter((addon) => addon.name);
    return {
      id,
      name,
      nameTitle: title,
      qty: Number.isInteger(item.qty) ? item.qty : 1,
      price,
      priceText: priceText.name,
      priceTitle: priceText.title,
      chosen: shownAddons(Array.isArray(item.addons_selected) ? item.addons_selected : []),
      toOffer: shownAddons(suggestedAddons(item)),
    };
  });
  const count = articles.reduce((sum, item) => sum + item.qty, 0);
  return { articles, count, total: parseAmount(cart.total) };
};

// O marcador da linha do nome: o componente traduz-lhe o rotulo.
export const PROFILE_NAME_KEY = "__profile_name__";

// Estreito de proposito: `code` solto apanhava `barcode` e `decoded`, que nao
// identificam ninguem. So `id`/`code` exactos, o sufixo separado (`_id`, `_code`,
// ` id`, `-id`) e o camelCase (`customerId`, `customerID`, `customerCode`).
const isIdentifierKey = (key) =>
  /^(id|code)$/i.test(key) || /(_code|[_ -]id)$/i.test(key) || /[a-z0-9](Id|ID|Code)$/.test(key);

/**
 * The customer profile in the order an operator reads it: the name first (first
 * and last name joined, or a lone `name`), then the email, then the identifiers,
 * then the rest as it came. Postgres hands the keys back shortest first, which put
 * the email above the name.
 */
export const orderProfileEntries = (entries) => {
  const value = (key) => entries.find(([k]) => k === key)?.[1];
  const usable = (v) => isScalar(v) && String(v).trim() !== "";
  const nameKeys = ["first_name", "last_name"].filter((k) => usable(value(k)));
  const parts = nameKeys.map(value);
  const lone = usable(value("name")) ? value("name") : null;
  const usedKeys = new Set();
  let name = null;
  if (parts.length) {
    name = parts.join(" ");
    // Um `name` igual ao nome composto e um duplicado exato e sai; se for diferente
    // fica numa linha propria.
    if (lone !== null && String(lone).trim() === name) usedKeys.add("name");
    // So se consomem as chaves que entraram no nome: uma que nao seja escalar
    // fica como linha propria.
    nameKeys.forEach((k) => usedKeys.add(k));
  } else if (lone !== null) {
    name = String(lone);
    usedKeys.add("name");
  }
  const rest = entries.filter(([k]) => !usedKeys.has(k));
  const email = rest.filter(([k]) => k.toLowerCase() === "email");
  const identifiers = rest.filter(([k]) => k.toLowerCase() !== "email" && isIdentifierKey(k));
  const others = rest.filter(([k]) => k.toLowerCase() !== "email" && !isIdentifierKey(k));
  return [...(name === null ? [] : [[PROFILE_NAME_KEY, name]]), ...email, ...identifiers, ...others];
};

/** The part of a BCP 47 tag before the first "-", in lower case ("pt-PT" gives "pt"). */
export const baseLanguage = (code) => code.split("-")[0].toLowerCase();
